import { randomBytes } from "@noble/hashes/utils.js";
import { decrypt, encrypt } from "../crypto/xchacha20poly1305.js";
import { deriveKey } from "../crypto/hkdf.js";
import {
  DeviceKeys,
  devicePublicKeyJwk,
  unwrapSyncKeyFromJwk,
  wrapSyncKeyForPeer,
  X25519PublicJwk,
} from "../crypto/keys.js";
import {
  generateX25519KeyPair,
  publicKeyFromJwk,
  publicKeyToJwk,
  x25519SharedSecret,
} from "../crypto/x25519.js";
import {
  ENROLLMENT_DEEP_LINK_SCHEME,
  ENROLLMENT_TTL_SECONDS,
} from "../protocol/constants.js";

const BUNDLE_KDF_INFO = "trustii-wallet-enrollment-v1";

export interface EnrollmentBundlePlaintext {
  v: 1;
  account_id: number;
  sync_device_id: string;
  public_key_jwk: X25519PublicJwk;
  wrapped_sync_key: string;
  exp: number;
  nonce: string;
}

export interface EphemeralHandshake {
  ephemeralPublicJwk: X25519PublicJwk;
  ephemeralPrivateKey: Uint8Array;
}

export interface EnrollmentWireBundle {
  trusted_public_key_jwk: X25519PublicJwk;
  kdf_nonce: string;
  content_nonce_base64: string;
  ciphertext_base64: string;
}

export function createEphemeralHandshake(): EphemeralHandshake {
  const keys = generateX25519KeyPair();
  return {
    ephemeralPublicJwk: publicKeyToJwk(keys.publicKey),
    ephemeralPrivateKey: keys.privateKey,
  };
}

export function deriveBundleKey(
  localPrivateKey: Uint8Array,
  peerPublicKey: Uint8Array,
  nonce: string,
): Uint8Array {
  const shared = x25519SharedSecret(localPrivateKey, peerPublicKey);
  return deriveKey(shared, nonce, BUNDLE_KDF_INFO);
}

export function createBundle(
  trustedDevice: DeviceKeys,
  accountId: number,
  newDevicePublicJwk: X25519PublicJwk,
  syncKey: Uint8Array,
  newSyncDeviceId: string,
  recipientEphemeralPublicJwk: X25519PublicJwk,
  fixedExp?: number,
): { bundle: EnrollmentBundlePlaintext; wire: EnrollmentWireBundle; deepLink: string } {
  const newDevicePublic = publicKeyFromJwk(newDevicePublicJwk);
  const wrappedSyncKey = wrapSyncKeyForPeer(
    syncKey,
    trustedDevice.x25519.privateKey,
    newDevicePublic,
  );
  const exp = fixedExp ?? Math.floor(Date.now() / 1000) + ENROLLMENT_TTL_SECONDS;
  const nonce = Buffer.from(randomBytes(16)).toString("hex");
  const bundle: EnrollmentBundlePlaintext = {
    v: 1,
    account_id: accountId,
    sync_device_id: newSyncDeviceId,
    public_key_jwk: newDevicePublicJwk,
    wrapped_sync_key: wrappedSyncKey,
    exp,
    nonce,
  };

  const recipientEphemeralPublic = publicKeyFromJwk(recipientEphemeralPublicJwk);
  const bundleKey = deriveBundleKey(
    trustedDevice.x25519.privateKey,
    recipientEphemeralPublic,
    nonce,
  );
  const plaintext = new TextEncoder().encode(JSON.stringify(bundle));
  const { ciphertext, nonce: encNonce } = encrypt(bundleKey, plaintext);

  const wire: EnrollmentWireBundle = {
    trusted_public_key_jwk: devicePublicKeyJwk(trustedDevice),
    kdf_nonce: nonce,
    content_nonce_base64: Buffer.from(encNonce).toString("base64"),
    ciphertext_base64: Buffer.from(ciphertext).toString("base64"),
  };

  return {
    bundle,
    wire,
    deepLink: encodeDeepLink(wire),
  };
}

export function consumeBundle(
  wire: EnrollmentWireBundle,
  newDeviceKeys: DeviceKeys,
  ephemeralPrivateKey: Uint8Array,
): { bundle: EnrollmentBundlePlaintext; syncKey: Uint8Array } {
  const trustedPublic = publicKeyFromJwk(wire.trusted_public_key_jwk);
  const encNonce = Buffer.from(wire.content_nonce_base64, "base64");
  const ciphertext = Buffer.from(wire.ciphertext_base64, "base64");
  const bundleKey = deriveBundleKey(ephemeralPrivateKey, trustedPublic, wire.kdf_nonce);
  const plaintext = decrypt(bundleKey, ciphertext, encNonce);
  const bundle = JSON.parse(new TextDecoder().decode(plaintext)) as EnrollmentBundlePlaintext;
  if (bundle.v !== 1) {
    throw new Error("unsupported_enrollment_bundle");
  }
  if (bundle.exp <= Math.floor(Date.now() / 1000)) {
    throw new Error("enrollment_bundle_expired");
  }
  if (bundle.sync_device_id !== newDeviceKeys.syncDeviceId) {
    throw new Error("enrollment_device_mismatch");
  }

  const syncKey = unwrapSyncKeyFromJwk(
    bundle.wrapped_sync_key,
    newDeviceKeys.x25519.privateKey,
    wire.trusted_public_key_jwk,
  );

  return { bundle, syncKey };
}

export function encodeDeepLink(wire: EnrollmentWireBundle): string {
  const encoded = Buffer.from(JSON.stringify(wire)).toString("base64url");
  return `${ENROLLMENT_DEEP_LINK_SCHEME}?b=${encoded}`;
}

export function parseDeepLink(url: string): EnrollmentWireBundle {
  const parsed = new URL(url);
  if (parsed.protocol !== "trustii:" || parsed.host !== "enroll") {
    throw new Error("invalid_enrollment_deep_link");
  }
  const bundle = parsed.searchParams.get("b");
  if (!bundle) {
    throw new Error("missing_enrollment_bundle");
  }
  return JSON.parse(Buffer.from(bundle, "base64url").toString("utf8")) as EnrollmentWireBundle;
}

export const Enrollment = {
  createEphemeralHandshake,
  createBundle,
  consumeBundle,
  encodeDeepLink,
  parseDeepLink,
};
